import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { flatten, uniqWith } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  getFormFieldData,
  isFormFieldOfArrayType,
  isFormFieldOfBooleanType,
  isFormFieldOfCandidateType,
  isFormFieldOfDateType,
  isFormFieldOfProjectClientType,
  isFormFieldOfProjectType,
  isFormFieldOfUserType,
} from '../common/filterHelpers';
import { Filter } from '../common/filters.type';
import { SearchFormFieldEnum } from '../common/searchFormField.types';
import { CriteriaOperatorEnum, SearchQueryCriteriaType, SearchQueryCriteriaValueType, UserType } from '../common/types';
import { useGetAllCandidates } from '../common/useGetAllCandidates';
import { useGetAllProjects } from '../common/useGetAllProjects';
import { useGetAllUsers } from '../common/useGetAllUsers';
import { CandidateType } from '../features/candidates/Candidates.types';
import { ProjectType } from '../features/projects/ProjectsTable.types';
import Icon, { ButtonIcon, iconColors } from '../icons/Icon';

import { StyledButton } from './Button';
import { StyledColumn } from './StyledColumn';
import { StyledRow } from './StyledRow';

const StyledTag = styled(Tag)`
  border-color: #e0e0e0;
  background-color: #ffffff;
  border-radius: 1rem;
  height: 37px;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  font-size: 14px;
`;

const ClearFilterButton = styled(Button)`
  height: 37px;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  font-size: 14px;
`;

const PlusIcon = styled(ButtonIcon).attrs({
  name: 'Plus',
  style: { color: `${iconColors.blue_20}` },
})``;

const RestoreIcon = styled(ButtonIcon).attrs({
  name: 'Restore',
})``;

const FilterClearIcon = styled(ButtonIcon).attrs({
  name: 'FilterClear',
})``;

const FilterResetIcon = styled(ButtonIcon).attrs({
  name: 'FilterReset',
})``;

const mapDateUnixToFormFormat = (date: number) => dayjs.unix(date).format('YYYY-MM-DD');

const getTagValueByCriterion = (
  criterion: SearchQueryCriteriaType,
  criteria: SearchQueryCriteriaType[],
  candidates: CandidateType[],
  users: UserType[],
  projects: ProjectType[]
) => {
  // take any start or end date (e.g. CreateDateStart, CreateDateEnd), find the second one and compose one tag string from those two
  if (isFormFieldOfDateType(criterion.field)) {
    const sameFieldCriteria = criteria
      .filter((element) => element.field === criterion.field)
      .map((element) => element.value)
      .sort((a, b) => (a as number) - (b as number));

    if (sameFieldCriteria.length === 2) {
      return `${mapDateUnixToFormFormat(sameFieldCriteria[0] as number)} - ${mapDateUnixToFormFormat(
        sameFieldCriteria[1] as number
      )}`;
    } else {
      return mapDateUnixToFormFormat(criterion.value as number);
    }
  }

  if (isFormFieldOfUserType(criterion.field)) {
    const user = users.find((user) => user.id === criterion.value);
    return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
  }

  if (isFormFieldOfCandidateType(criterion.field)) {
    const candidate = candidates.find((candidate) => candidate.uuid === criterion.value);
    return `${candidate?.lastName ?? ''} ${candidate?.firstName ?? ''}`;
  }

  if (isFormFieldOfProjectType(criterion.field)) {
    const project = projects.find((project) => project.uuid === criterion.value);
    return project?.name || '';
  }

  if (isFormFieldOfProjectClientType(criterion.field)) {
    const project = projects.find((project) => project.uuid === criterion.value);
    return project?.name || '';
  }

  // e.g. map enum array [STATIONARY, REMOTE_ONLY] to string: Stationary, Remote only
  if (isFormFieldOfArrayType(criterion.field)) {
    return getFormFieldData(criterion.field)
      .toFormValueFormatter?.(flatten([criterion.value]) as string[])
      .join(', ');
  }

  return `${criterion.value}`;
};

type FilterTagsProps = {
  criteria: SearchQueryCriteriaType[];
  name: string;
  resetFilter?: () => void;
  onClose: (field: string, value?: SearchQueryCriteriaValueType) => void;
  resetFilterToCurrent: () => void;
  reloadData: () => void;
};

const FilterTags: React.FC<FilterTagsProps> = ({
  name,
  criteria,
  onClose,
  resetFilter,
  resetFilterToCurrent,
  reloadData,
}) => {
  const { data: candidatesData } = useGetAllCandidates();
  const { data: usersData } = useGetAllUsers();
  const { data: projectsData } = useGetAllProjects();

  const handleClose = (field: string, value?: SearchQueryCriteriaValueType) => {
    onClose(field, value);
  };

  const shouldBeRemovedByField = (field: SearchFormFieldEnum) =>
    isFormFieldOfArrayType(field) || isFormFieldOfDateType(field);

  return (
    <StyledColumn>
      <StyledRow justifyContent="start" marginTop="10px">
        <Typography.Title level={5}>Filter name: </Typography.Title>
        <Typography.Text>{name}</Typography.Text>
      </StyledRow>
      <StyledRow justifyContent="start" flexWrap="wrap">
        {uniqWith(
          criteria.map((criterion) => ({
            criterionField: criterion.field,
            criterionValue: criterion.value,
            label: getFormFieldData(criterion.field).label,
            value: !isFormFieldOfBooleanType(criterion.field)
              ? getTagValueByCriterion(
                  criterion,
                  criteria,
                  candidatesData?.results || [],
                  usersData?.results || [],
                  projectsData?.results || []
                )
              : '',
          })),
          (tagData1, tagData2) => tagData1.label === tagData2.label && tagData1.value === tagData2.value
        ).map((tagData) => (
          <StyledTag
            key={`${tagData.criterionField}_${tagData.criterionValue}`}
            closable
            closeIcon={<Icon name="Close" style={{ marginLeft: '5px' }} />}
            onClose={(e) => {
              e.preventDefault();
              handleClose(
                tagData.criterionField,
                shouldBeRemovedByField(tagData.criterionField) ? undefined : tagData.criterionValue
              );
            }}
          >
            <Typography.Text type="secondary">{tagData.label}</Typography.Text>
            {!isFormFieldOfBooleanType(tagData.criterionField) && <span>:&nbsp;</span>}
            <Typography.Text>{tagData.value}</Typography.Text>
          </StyledTag>
        ))}
        {criteria.length > 0 && (
          <ClearFilterButton
            icon={<FilterClearIcon />}
            onClick={resetFilter}
          >
            Clear filters
          </ClearFilterButton>
        )}
        <ClearFilterButton icon={<FilterResetIcon />} onClick={resetFilterToCurrent}>
          Reset filters
        </ClearFilterButton>
        <ClearFilterButton icon={<RestoreIcon />} onClick={reloadData}>
          Reload
        </ClearFilterButton>
      </StyledRow>
    </StyledColumn>
  );
};

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;

  @media (min-width: 576px) {
    gap: 25px;
  }

  @media (min-width: 992px) {
    margin-bottom: 0;
  }
`;

const StyledTitle = styled(Typography.Title)`
  &.ant-typography {
    font-size: 20px;
    margin: 0;

    @media (min-width: 992px) {
      font-size: 24px;
    }
  }
`;

const HeaderButtons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 5px;

  @media (min-width: 576px) {
    gap: 15px;
  }

  @media (min-width: 992px) {
    order: 1;
  }
`;

const StyledFindAllContainer = styled.div`
  width: 100%;

  @media (min-width: 992px) {
    width: auto;
    margin-top: 6px;
    margin-left: 24px;
    width: 240px;
  }
`;

type FilteredTableHeaderProps = {
  title: string;
  currentFilter: Filter;
  addPageRoute?: string;
  headerActions?: React.ReactNode;
  showFilters: () => void;
  resetFilter?: () => void;
  addCriteria?: (value: SearchQueryCriteriaType, overwrite: boolean) => void;
  deleteCriteria: (field: string, value?: SearchQueryCriteriaValueType) => void;
  resetFilterToCurrent: () => void;
  reloadData: () => void;
};

export const Header: React.ForwardRefRenderFunction<HTMLDivElement, FilteredTableHeaderProps> = (
  {
    title,
    showFilters,
    resetFilter,
    currentFilter,
    addCriteria,
    deleteCriteria,
    addPageRoute,
    headerActions,
    resetFilterToCurrent,
    reloadData,
  },
  ref
) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const queryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (queryTimeoutRef.current) {
        clearTimeout(queryTimeoutRef.current);
      }
    };
  }, []);

  const onChangeQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current);
    }
    queryTimeoutRef.current = setTimeout(() => {
      const queryValue = e.target.value;
      setQuery(queryValue);
      if (!queryValue) {
        deleteCriteria?.(SearchFormFieldEnum.All, query);
        return;
      }
      function mapStringToGlobalQuery(value: string) {
        return {
          field: SearchFormFieldEnum.All,
          operator: CriteriaOperatorEnum.LK,
          value,
        };
      }
      addCriteria?.(mapStringToGlobalQuery(queryValue), true);
    }, 500);
  };

  return (
    <div ref={ref} style={{ padding: '16px 0px 12px 0px' }}>
      <StyledHeaderContainer>
        <StyledTitle level={3}>{title}</StyledTitle>

        <HeaderButtons>
          {headerActions}
          <StyledButton
            icon={<ButtonIcon name="Filter" style={{ color: `${iconColors.blue_20}` }} />}
            onClick={showFilters}
          >
            Filters
          </StyledButton>
          {addPageRoute && (
            <StyledButton
              icon={<PlusIcon style={{ color: `${iconColors.blue_20}` }} />}
              onClick={() => navigate(addPageRoute)}
            >
              Add
            </StyledButton>
          )}
        </HeaderButtons>

        <StyledFindAllContainer>
          <Input placeholder="Search all" onChange={onChangeQuery}></Input>
        </StyledFindAllContainer>
      </StyledHeaderContainer>

      <FilterTags
        criteria={currentFilter.criteria}
        name={currentFilter.name}
        onClose={deleteCriteria}
        resetFilter={resetFilter}
        resetFilterToCurrent={resetFilterToCurrent}
        reloadData={reloadData}
      />
    </div>
  );
};

export const FilteredTableHeader = React.forwardRef<HTMLDivElement, FilteredTableHeaderProps>(Header);
